import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, Trash2 } from "lucide-react";

export type PinMenuState = { x: number; y: number; lat: number; lng: number; label?: string } | null;

/**
 * Right-click context menu for a searched-location pin (LiveMap / ReplayMap).
 * Rendered via portal to document.body — both maps' outer containers use
 * `isolate` (to trap Leaflet's own z-index escaping above page dropdowns),
 * which also traps `position: fixed` descendants into that stacking context.
 * A portal sidesteps that so this menu reliably renders above everything.
 */
export function PinContextMenu({ menu, onClose, onRemove }: { menu: PinMenuState; onClose: () => void; onRemove: () => void }) {
  if (!menu) return null;

  const copyCoords = async () => {
    const text = `${menu.lat.toFixed(6)}, ${menu.lng.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Coordinates copied");
    } catch {
      toast.error("Could not copy coordinates");
    }
    onClose();
  };

  const copyLabel = async () => {
    try {
      await navigator.clipboard.writeText(menu.label ?? "");
      toast.success("Address copied");
    } catch {
      toast.error("Could not copy address");
    }
    onClose();
  };

  const openInGoogleMaps = () => {
    window.open(`https://www.google.com/maps?q=${menu.lat},${menu.lng}`, "_blank", "noopener,noreferrer");
    onClose();
  };

  return createPortal(
    <>
      {/* Full-screen click-catcher to close on outside click, under the menu itself */}
      <div className="fixed inset-0 z-[2000]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        className="fixed z-[2001] min-w-[180px] overflow-hidden rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
        style={{ left: menu.x, top: menu.y }}
      >
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted"
          onClick={copyCoords}
        >
          <Copy className="h-3.5 w-3.5" /> Copy coordinates
        </button>
        {menu.label ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted"
            onClick={copyLabel}
          >
            <Copy className="h-3.5 w-3.5" /> Copy address
          </button>
        ) : null}
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted"
          onClick={openInGoogleMaps}
        >
          <ExternalLink className="h-3.5 w-3.5" /> Open in Google Maps
        </button>
        <div className="my-1 h-px bg-border" />
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-destructive hover:bg-destructive/10"
          onClick={() => { onRemove(); onClose(); }}
        >
          <Trash2 className="h-3.5 w-3.5" /> Remove pin
        </button>
      </div>
    </>,
    document.body,
  );
}
